import * as React from 'react';

import { Badge, Row, Tab } from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { useFacilityQuery } from './_graphql/useFacilityQuery';
import { useFacilityRolesQuery } from './_graphql/useFacilityRolesQuery';
import { FacilityRolesTab } from './FacilityRolesTab';
import { FacilityUsersTab } from './FacilityUsersTab';

import { SettingsSubmodule } from '~/modules/settings/_components/_layouts/SettingsSubmodule';
import {
  $currentFacility,
  $facilityDetailsSelectedRoleId,
  $facilityDetailsSelectedUserId,
  $facilityDetailsTab,
  $teamPermissionsFacilityId,
  type FacilityDetailsTab,
} from '~/modules/settings/_state/facilityDetailsForm.state';
import { useSettingsLocation } from '~/modules/settings/_state/settingsModule.state';
import { useTranslations } from '~/modules/settings/i18n/useTranslations';
import { usePermission } from '~/shared/hooks/usePermission';

export const TeamPermissionsFacilityDetailsView = () => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();
  const facilityId = useAtomValue($teamPermissionsFacilityId);
  const [currentTab, setCurrentTab] = useAtom($facilityDetailsTab);
  const currentFacility = useAtomValue($currentFacility);
  const setCurrentFacility = useSetAtom($currentFacility);
  const setSelectedRoleId = useSetAtom($facilityDetailsSelectedRoleId);
  const setSelectedUserId = useSetAtom($facilityDetailsSelectedUserId);

  const facilityScope = React.useMemo(
    () =>
      facilityId
        ? { resource: 'facility' as const, resourceId: facilityId }
        : undefined,
    [facilityId],
  );
  const roleRead = usePermission('roleRead', facilityScope);
  const userRead = usePermission('userRead', facilityScope);
  const permissions = {
    canReadRoles: roleRead.allowed,
    canReadUsers: userRead.allowed,
  };

  const { data } = useFacilityQuery({
    facilityId: facilityId ?? '',
  });

  const { data: rolesData } = useFacilityRolesQuery({
    facilityId: facilityId ?? '',
    limit: 1000,
    offset: 0,
  });

  const totalUsers = React.useMemo(() => {
    if (!rolesData?.users) return 0;
    const uniqueUserIds = new Set<string>();
    rolesData.users.forEach((users) => {
      users.forEach((user) => uniqueUserIds.add(user.id));
    });
    return uniqueUserIds.size;
  }, [rolesData]);

  React.useEffect(() => {
    if (data?.facility) {
      setCurrentFacility(data.facility);
    }
  }, [data, setCurrentFacility]);

  React.useEffect(
    () => () => {
      setCurrentFacility(null);
      setSelectedRoleId(null);
      setSelectedUserId(null);
    },
    [setCurrentFacility, setSelectedRoleId, setSelectedUserId],
  );

  const handleBack = () => {
    setLocation('/team-and-permissions/facilities');
  };

  const handleTabChange = (tab: FacilityDetailsTab) => {
    setCurrentTab(tab);
  };

  return (
    <SettingsSubmodule
      className="@TeamPermissionsFacilityDetailsView h-full"
      contentClassName="p-0"
      title={currentFacility?.name ?? 'Loading...'}
      subtitle={currentFacility?.address ?? ''}
      onBackPressed={handleBack}
      tabs={
        <>
          {permissions.canReadRoles && (
            <Tab
              selected={currentTab === 'roles'}
              onClick={() => handleTabChange('roles')}
            >
              {t.teamAndPermissions.facilities.details.tabs.roles}
            </Tab>
          )}
          {permissions.canReadUsers && (
            <Tab
              selected={currentTab === 'users'}
              onClick={() => handleTabChange('users')}
            >
              <Row className="items-center gap-1.5">
                {t.teamAndPermissions.facilities.details.tabs.users}
                {totalUsers > 0 && (
                  <Badge color="neutral" className="text-xs">
                    {totalUsers}
                  </Badge>
                )}
              </Row>
            </Tab>
          )}
        </>
      }
    >
      {currentTab === 'roles' && permissions.canReadRoles && (
        <FacilityRolesTab />
      )}
      {currentTab === 'users' && permissions.canReadUsers && (
        <FacilityUsersTab />
      )}
    </SettingsSubmodule>
  );
};
